import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FlashcardDisplay } from "./FlashcardDisplay";
import { QuizDisplay } from "./QuizDisplay";
import type { DocumentAiResults } from "@/types";

interface DocumentDetailViewProps {
  aiResults: DocumentAiResults;
}

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function CollapsibleSection({ title, children, defaultOpen = false }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Card>
      <CardHeader
        className="cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        aria-expanded={isOpen}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{title}</CardTitle>
          <Button variant="ghost" size="sm" tabIndex={-1}>
            {isOpen ? "▲" : "▼"}
          </Button>
        </div>
      </CardHeader>
      {isOpen && <CardContent>{children}</CardContent>}
    </Card>
  );
}

export function DocumentDetailView({ aiResults }: DocumentDetailViewProps) {
  return (
    <div className="space-y-4">
      {/* Summaries */}
      <CollapsibleSection title="Short Summary" defaultOpen>
        <p className="text-sm">{aiResults.shortSummary}</p>
      </CollapsibleSection>

      <CollapsibleSection title="Medium Summary">
        <p className="text-sm">{aiResults.mediumSummary}</p>
      </CollapsibleSection>

      <CollapsibleSection title="Long Summary">
        <p className="text-sm whitespace-pre-wrap">{aiResults.longSummary}</p>
      </CollapsibleSection>

      {/* Key Points */}
      <CollapsibleSection title="Key Points" defaultOpen>
        {(aiResults.keyPoints?.length ?? 0) > 0 ? (
          <ul className="list-disc space-y-1 pl-5">
            {aiResults.keyPoints.map((point, index) => (
              <li key={index} className="text-sm">
                {point}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No key points available.</p>
        )}
      </CollapsibleSection>

      {/* Flashcards */}
      <CollapsibleSection title="Flashcards">
        <FlashcardDisplay flashcards={aiResults.flashcards} />
      </CollapsibleSection>

      {/* Quiz */}
      <CollapsibleSection title="Quiz">
        <QuizDisplay questions={aiResults.quizQuestions} />
      </CollapsibleSection>
    </div>
  );
}
